import { h, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { toJS } from 'mobx';
import { Text, Localizer } from 'preact-i18n';
import Markdown from 'preact-markdown';
import { route } from 'preact-router';

import localUtil from '../../lib/util';
import { resolveEventColor } from '../../lib/eventColor';

const DEFAULT_HEIGHT = 80; // 60 minutes per hour * 1.2px per minute
const DEFAULT_HEIGHT_SCALE = DEFAULT_HEIGHT / 60;

//  'var(--bs-body-color)' : 'var(--bs-body-color)', darkmode ? 'var(--bs-body-bg)' : 'var(--bs-body-bg)')

const AVAILABILITY_COLOR = 'var(--bs-body-bg)';
const NO_AVAILABILITY_COLOR = 'rgba(var(--bs-secondary-bg-rgb), 0.9)';

// Map preset names to CSS colors (Bootstrap 5 variables for light/dark mode compatibility)
const OFF_DUTY_COLORS = {
    '': 'rgba(var(--bs-secondary-bg-rgb), 0.9)',          // Light (current default)
    'medium': 'rgba(var(--bs-emphasis-color-rgb), 0.08)',  // Medium
    'dark': 'rgba(var(--bs-emphasis-color-rgb), 0.15)',    // Dark
    'veryDark': 'rgba(var(--bs-emphasis-color-rgb), 0.25)',// Very Dark
};
const ONLINE_BOOKING_AVAILABILITY_COLOR = 'rgba(13, 202, 240, 0.25)'; // info/cyan color for online booking
const BORDER_COLOR = 'var(--bs-dark-bg-subtle)';

const formatTime = iso => iso ? new Date(iso).toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit' }) : '-';
const clampPos = (x, y, w = 320, h = 180, m = 12) => {
    const vw = typeof window !== 'undefined' ? window.innerWidth : 1024;
    const vh = typeof window !== 'undefined' ? window.innerHeight : 768;
    let left = x + m;
    let top = y + m;
    if (left + w > vw) left = Math.max(m, vw - w - m);
    if (top + h > vh) top = Math.max(m, vh - h - m);
    return { left, top };
};

// Optional: trim long text
const ellipsize = (str, max = 140) => {
    if (!str) return '';
    return str.length > max ? `${str.slice(0, max - 1)}…` : str;
};

// --- small preview component ---
const EventPreview = ({ ev, currentVisitor, pos, color, bgColor }) => {
    const width = 320;
    const height = 'auto';
    return (
        <div
            class='position-fixed shadow rounded-lg'
            style={`
                left: ${pos.left}px;
                top: ${pos.top}px;
                width: ${width}px;
                max-width: 90vw;
                pointer-events: none; /* tooltip behavior */
                z-index: 10001;
                background: ${bgColor || '#fff'};
                color: ${color || '#222'};
                border: 1px solid rgba(0,0,0,0.1);
                padding: 10px 12px;
                line-height: 1.2;
            `}
            role='tooltip'
        >
            <div class='d-flex align-items-center mb-1'>
                <div class='me-2' style={`width: 8px; height: 24px; background: ${color}; border-radius: 2px;`} />
                <div class='font-weight-bold' style='font-size: 0.95rem;'>
                    {ev.skipReminders && <i class='fa-duotone fa-bell-slash text-danger me-1' />}
                    {ev.isRecurring && <i class='fa-duotone fa-repeat me-1' />}
                    {ev.title || 'Uten tittel'}
                </div>
            </div>
            <div class='text-muted' style='font-size: 0.85rem;'>
                {formatTime(ev.start)}–{formatTime(ev.end)}
                {ev.location && <> · {ev.location}</>}
            </div>
            {ev.body && <div class='mt-2' style='font-size: 0.9rem;'>{ellipsize(ev.body, 180)}</div>}

            {currentVisitor && currentVisitor.firstname && <>
                <div class='text-muted mt-2 pt-2 border-top' style='font-size: 0.85rem;'>
                    {currentVisitor.firstname} {currentVisitor.lastname}<br />
                    {currentVisitor.address}, {currentVisitor.postalcode} {currentVisitor.place}<br />
                    {currentVisitor.cellphone}
                </div>
            </>}
        </div>
    );
};

@observer
class CalendarHourGrid extends Component {
  	constructor(props) {
        super(props);
        this.state = {
            isDragging: false,
            hoverEvent: null,
            previewPos: { left: 0, top: 0 },
            isShiftPressed: false,
        };
        this.hoverShowTimer = null;
        this.hoverHideTimer = null;
    }

    getHeightConfig = () => {
        const { userStore } = this.props.stores;
        const { customer = {} } = userStore;
        const { featureFlags = {} } = customer;
        const HEIGHT = parseInt(featureFlags.calendarHourHeight, 10) || DEFAULT_HEIGHT;
        const HEIGHT_SCALE = HEIGHT / 60;
        return { HEIGHT, HEIGHT_SCALE };
    }

    loadAll = () => {
        const { calendarStore, calendarEventStore } = this.props.stores;
        calendarStore.getHoursInDay();
    }

    editcalendarEvent = e => {
        e.stopPropagation();
        e.preventDefault();
        const { id } = e.target.closest('button').dataset;
		const { drawerLevel = 1, callback = () => {} } = this.props;
		const { appState, calendarEventStore } = this.props.stores;
        const { calendarEvent = {}, calendarEvents = [], visitors, animals } = calendarEventStore;
        // Try to find the event from calendarEvents list if calendarEvent is not set
        const event = calendarEvent?.id ? calendarEvent : calendarEvents.find(ev => ev.id === parseInt(id, 10)) || {};
		const { drawerHeightLarge } = appState;
		appState.openDrawer('editCalendarEvent', {
			height: drawerHeightLarge,
            calendarType: event.calendarType,
            calendarId: event.calendarId,
            startDate: event.start,
			id,
            callback: event => {
                console.log('editcalendarEvent.callback');
                // callback(event);
            },
		}, drawerLevel + 3);
	}

    viewEvent = e => {
        e.stopPropagation();
        e.preventDefault();

        const { showViewEvent = true } = this.props;
        if (!showViewEvent) {
            return;
        }

        // Conventions are synthetic events (id 'convention-<id>'); open the
        // convention page instead of the booking drawer.
        const { conventionId } = e.target.closest('.calendar-event').dataset;
        if (conventionId) {
            route(`/convention/${conventionId}`);
            return;
        }

        const { id, start: eventStart } = e.target.closest('.calendar-event').dataset;
        console.log('viewEvent clicked, id:', id);

        // Validate that we have a valid ID
        if (!id || id === 'undefined' || id === 'null') {
            console.warn('No valid event ID found on clicked event');
            return;
        }

        const parsedId = parseInt(id, 10);
        if (isNaN(parsedId)) {
            console.warn('Invalid event ID:', id);
            return;
        }

        const { appState, calendarStore, calendarEventStore } = this.props.stores;
        const { eventId } = calendarStore
        if (eventId) {
            return;
        }

        const { drawerLevel = 1, callback = () => {} } = this.props;
        const { drawerHeightMediumLarge } = appState;

        appState.openDrawer('viewCalendarEvent', {
            height: drawerHeightMediumLarge,
            id: parsedId,
            eventStart: eventStart || null,
            showEdit: true,
            callback: event => {
                if (event?.excludeDate) {
                    const dateStr = new Date(eventStart).toISOString().slice(0, 10);
                    const filtered = calendarEventStore.calendarEvents.filter(e => {
                        if (e.id !== event.id) return true;
                        const eDate = new Date(e.start).toISOString().slice(0, 10);
                        return eDate !== dateStr;
                    });
                    calendarEventStore.updateKeyValue('calendarEvents', filtered);
                }
                callback(event);
                appState.toggleDrawer(false, drawerLevel + 2);
                calendarStore.resetDragging();
            },
        }, drawerLevel + 2);
    }

    selectTime = e => {
        const { HEIGHT, HEIGHT_SCALE } = this.getHeightConfig();
        const { isDragging } = this.state;
        if (isDragging) {
            return;
        }
        const { enableSelection } = this.props;
        if (!enableSelection) {
            return;
        }
        e.stopPropagation();
        e.preventDefault();
        const { hour, date } = e.target.closest('.hour-line').dataset;
    // console.log('selectTime', { hour, date })
        const rect = e.currentTarget.getBoundingClientRect();
        const y = e.clientY - rect.top;  // y position within the element


        // Round to the nearest 15 minutes
        const roundedMinutes = Math.round((y / HEIGHT_SCALE) / 15) * 15;
        // Zero pad minutes if needed
        let paddedMinute = roundedMinutes < 10 ? `0${roundedMinutes}` : roundedMinutes;
        let hourInt = parseInt(hour, 10);
        // console.log('y', y, 'roundedMinutes', roundedMinutes, 'hourInt', hourInt)
        if (roundedMinutes === 60) {
            hourInt += 1;
            // console.log('y', y, 'yRelative', yRelative, 'minScale', minScale, 'roundedMinutes', roundedMinutes, 'hourInt', hourInt)
            paddedMinute = '00';
        }
        const paddedHour = hourInt < 10 ? `0${hourInt}` : hourInt;

        const { appState, saleStore, calendarStore } = this.props.stores;
        // const { calendarDate } = calendarStore;
        const { calendarDate } = this.props;
        // calendarStore.resetDragging();

        const startDate = new Date(`${util.isoDate(calendarDate, false, false, true)}T${paddedHour}:${paddedMinute}:00`);
        const endDate = new Date(startDate);
        endDate.setMinutes(endDate.getMinutes() + 15);
        // console.log(`paddedHour: ${paddedHour}, paddedMinute: ${paddedMinute}`)
        // console.log(`startDate: ${startDate}, endDate: ${endDate}`)
        const { calendarSelectDateTime, calendarId, callback = () => {} } = this.props;

        if (calendarSelectDateTime) {
            calendarSelectDateTime({
                start: startDate,
                end: endDate,
                calendarId,
            });
            return;
        }

        const { drawerLevel = 1, skipAnimal } = this.props;
        const { drawerHeightLarge } = appState;
        const { newSale } = saleStore;
        const { animals } = newSale;
        appState.openDrawer('newCalendarEvent', {
			height: drawerHeightLarge,
            isNew: true,
            hideAdmin: true,
            calendarId,
            calendarType: 'booking',
            animals,
            startDate: startDate,
            endDate: endDate,
            skipAnimal,
            callback: async booking => {
                // console.log('newCalendarEvent callback', booking);
                appState.toggleDrawer(false, drawerLevel + 1);
                callback(booking);
                calendarStore.resetDragging();
            },
		}, drawerLevel + 1);

    }

    handleMouseDown = e => {
        const { HEIGHT, HEIGHT_SCALE } = this.getHeightConfig();
        // console.log('handleMouseDown');
        const  { calendarId, showPullDownHandle, showViewEvent = true, enableSelection = true } = this.props;
        const { calendarStore } = this.props.stores;
        const { selectionStartDate, selectionEndDate, selectionCalendarId } = calendarStore;
        const selectionDiv = e.target.closest('.selection-calendar-event')
        const pullDownHandle = e.target.closest('.pull-down-handle');
        const eventDiv = e.target.closest('.calendar-event');

// console.log('handleMouseDown', { showViewEvent, enableSelection, selectionStartDate, selectionEndDate, selectionCalendarId, selectionDiv, pullDownHandle, eventDiv })

        if (pullDownHandle) {
            return this.handleMouseDownPullDownHandle(e);
        }
        if (selectionDiv) {
            return this.clickSelection(e);
        }
        if (eventDiv) {
            // If shift key is pressed, enable dragging even if showViewEvent is true
            if (showViewEvent && !e.shiftKey) {
                return this.viewEvent(e);
            }
            return this.handleMouseDownInsideEvent(e);
        }

        if (!enableSelection) {
            return;
        }
// console.log('handleMouseDown: isDragging');
        this.setState({
            isDragging: false,
        });

        const { hour, date } = e.target.closest('.hour-line').dataset;
// console.log('handleMouseDown', { hour, date })
        const rect = e.target.closest('.hour-line').getBoundingClientRect();
        const y = e.clientY - rect.top;  // y position within the element
        // Round to the nearest 15 minutes
        let roundedMinutes = Math.round((y / HEIGHT_SCALE) / 15) * 15;
        // Zero pad minutes if needed
        let paddedMinute = roundedMinutes < 10 ? `0${roundedMinutes}` : roundedMinutes;
        let hourInt = parseInt(hour, 10);
        if (paddedMinute === 60) {
            hourInt += 1;
            paddedMinute = '00';
            roundedMinutes = 0;
        }

        // const { calendarDate } = calendarStore;
        const { calendarDate } = this.props;

        const paddedHour = hourInt < 10 ? `0${hourInt}` : hourInt;

        // console.log('handleMouseDown', { hour, paddedHour, paddedMinute })
        const startDate = new Date(`${util.isoDate(calendarDate, false, false, true)}T${paddedHour}:${paddedMinute}:00`);
        const endDate = new Date(startDate);
        endDate.setMinutes(endDate.getMinutes() + 15);

// console.log('handleMouseDown', { startDate, endDate, calendarId })

        calendarStore.setSelectionStartDate(startDate);
        calendarStore.setSelectionEndDate(endDate);
        calendarStore.setSelectionCalendarId(calendarId);
        calendarStore.setDragging(true);
        calendarStore.setStartY(roundedMinutes * HEIGHT_SCALE);
        calendarStore.setStartYAbs(e.clientY * HEIGHT_SCALE);
        calendarStore.setEndY(e.clientY * HEIGHT_SCALE);
    }

    handleMouseDownInsideEvent = e => {
        e.stopPropagation();
        e.preventDefault();

        const { id } = e.target.closest('.calendar-event').dataset;
        const { calendarStore, calendarEventStore } = this.props.stores;
        const { calendarDate } = this.props;
        const eventId = parseInt(id, 10);
        const event = calendarEventStore.calendarEvents.find(e => e.id === eventId);

        if (!event) return;

        // Don't allow dragging recurring events
        if (event.isRecurring) {
            return;
        }

        // Don't allow dragging cancelled (offline) events — they are read-only until restored
        if (event.offline >= 1) {
            return;
        }

        // Calculate duration in minutes
        const startDate = new Date(event.start);
        const endDate = new Date(event.end);
        const duration = (endDate - startDate) / (1000 * 60); // Duration in minutes

        // Set drag state
        calendarStore.setIsDraggingEvent(true);
        calendarStore.setDraggedEventId(eventId);
        calendarStore.setDraggedEventOriginalStart(startDate);
        calendarStore.setDraggedEventDuration(duration);
        calendarStore.setDraggedEventOriginalCalendarId(event.calendarId);

        // Store the initial mouse Y position
        calendarStore.setStartYAbs(e.clientY);

        // Attach global mouse handlers for cross-calendar dragging
        document.addEventListener('mousemove', this.handleGlobalMouseMove);
        document.addEventListener('mouseup', this.handleGlobalMouseUp);
    }

    handleMouseMove = (e) => {
        const { HEIGHT, HEIGHT_SCALE } = this.getHeightConfig();
        this.setState({
            isDragging: true,
        });
        const { calendarStore, calendarEventStore } = this.props.stores;
        const {
            calendarDate,
            colors,
            calendarHours,
            startY,
            startYAbs,
            endY,
            selectionStartDate,
            selectionEndDate,
            dragging,
            eventId,
            eventEnd,
            isDraggingEvent,
            draggedEventId,
            draggedEventOriginalStart,
            draggedEventDuration,
        } = calendarStore;

        // Handle dragging an event to move it
        if (isDraggingEvent && draggedEventId) {
            const deltaY = e.clientY - startYAbs;
            const deltaMinutes = Math.round((deltaY / HEIGHT_SCALE) / 15) * 15;

            const event = calendarEventStore.calendarEvents.find(e => e.id === draggedEventId);
            if (event) {
                const newStart = new Date(draggedEventOriginalStart);
                newStart.setMinutes(newStart.getMinutes() + deltaMinutes);

                const newEnd = new Date(newStart);
                newEnd.setMinutes(newEnd.getMinutes() + draggedEventDuration);

                calendarEventStore.updateField(draggedEventId, 'start', newStart);
                calendarEventStore.updateField(draggedEventId, 'end', newEnd);
            }
            return;
        }

        if (dragging) {
            calendarStore.setEndY(e.clientY * HEIGHT_SCALE);
            const selectHeight = endY - startYAbs < 0 ? -Math.abs(endY - startYAbs) : Math.abs(endY - startYAbs);

            // let roundedMinutes = Math.round((y / HEIGHT_SCALE) / 15) * 15;
            // console.log('handleMouseMove', { dragging, startY, startYAbs, endY, eventId, eventEnd, selectHeight })

            const roundedSelectHeight = Math.round((selectHeight / HEIGHT_SCALE) / 15) * 15;

            const endDate = new Date(selectionStartDate);
            endDate.setMinutes(endDate.getMinutes() + roundedSelectHeight);
            calendarStore.setSelectionEndDate(endDate);

            if (eventId) {
                // Round to the nearest 15 minutes for resize feedback
                const roundedMinutesDiff = Math.round((selectHeight / HEIGHT_SCALE) / 15) * 15;

                const event = calendarEventStore.calendarEvents.find(e => e.id === eventId);
                const end = new Date(eventEnd);
                end.setMinutes(end.getMinutes() + roundedMinutesDiff);
                calendarEventStore.updateField(eventId, 'end', end);
            }
        }
    }

    handleMouseUp = e => {
        const { HEIGHT, HEIGHT_SCALE } = this.getHeightConfig();
        const { isDragging } = this.state;
        if (isDragging) {
            setTimeout(() => {
                this.setState({
                    isDragging: false,
                });
            }, 350);
        }
        const { calendarStore, calendarEventStore } = this.props.stores;

        const {
            selectionStartDate,
            selectionEndDate,
            selectionCalendarId,
            startYAbs,
            endY,
            eventId,
            eventEnd,
            isDraggingEvent,
            draggedEventId,
        } = calendarStore;

        // Skip event drag handling - the global handler (handleGlobalMouseUp) takes care of it
        if (isDraggingEvent && draggedEventId) {
            return;
        }

        calendarStore.setDragging(false);

        const { calendarSelectDateTime, calendarId, enableSelection } = this.props;
        // console.log('handleMouseUp', { enableSelection, calendarSelectDateTime, selectionStartDate, selectionEndDate, selectionCalendarId, startYAbs, endY, eventId, eventEnd })
        if (enableSelection && calendarSelectDateTime) {
            calendarSelectDateTime({
                start: selectionStartDate,
                end: selectionEndDate,
                calendarId: selectionCalendarId,
            });
            // calendarStore.resetDragging();
            // calendarStore.setSelectionStartDate(null)
            // calendarStore.setSelectionEndDate(null)
            return;
        }
        if (eventId) {
            e.preventDefault();
            e.stopPropagation();
            const selectHeight = endY - startYAbs < 0 ? -Math.abs(endY - startYAbs) : Math.abs(endY - startYAbs);
            const roundedSelectHeight = Math.round((selectHeight / HEIGHT_SCALE) / 15) * 15;

            const event = calendarEventStore.calendarEvents.find(e => e.id === eventId);
            const end = new Date(eventEnd);
            end.setMinutes(end.getMinutes() + roundedSelectHeight);
            calendarEventStore.save({
                end,
            }, eventId);
            calendarEventStore.updateField(eventId, 'end', end);
            setTimeout(() => {
                calendarStore.setEventId(null);
                calendarStore.setEndOfEvent(null);
            }, 500);
        }
    }

    handleGlobalMouseMove = (e) => {
        const { HEIGHT, HEIGHT_SCALE } = this.getHeightConfig();
        const { calendarStore, calendarEventStore } = this.props.stores;
        const {
            isDraggingEvent,
            draggedEventId,
            draggedEventOriginalStart,
            draggedEventDuration,
            startYAbs,
        } = calendarStore;

        if (!isDraggingEvent || !draggedEventId) return;

        const deltaY = e.clientY - startYAbs;
        const deltaMinutes = Math.round((deltaY / HEIGHT_SCALE) / 15) * 15;

        const event = calendarEventStore.calendarEvents.find(e => e.id === draggedEventId);
        if (!event) return;

        // Detect which calendar and date we're over
        const targetCalendarId = this.getCalendarIdAtPosition(e.clientX, e.clientY);
        const targetCalendarDate = this.getCalendarDateAtPosition(e.clientX, e.clientY);

        // Calculate new times based on target date (for week mode) and delta minutes
        const newStart = new Date(draggedEventOriginalStart);

        // If we have a target date (week mode), use that date but keep original time
        if (targetCalendarDate) {
            const targetDate = new Date(targetCalendarDate);
            newStart.setFullYear(targetDate.getFullYear());
            newStart.setMonth(targetDate.getMonth());
            newStart.setDate(targetDate.getDate());
        }

        // Apply time delta
        newStart.setMinutes(newStart.getMinutes() + deltaMinutes);

        const newEnd = new Date(newStart);
        newEnd.setMinutes(newEnd.getMinutes() + draggedEventDuration);

        // Update drag target for visual feedback
        calendarStore.setDragTargetCalendarId(targetCalendarId);

        // Update event times
        calendarEventStore.updateField(draggedEventId, 'start', newStart);
        calendarEventStore.updateField(draggedEventId, 'end', newEnd);

        // Update calendar if we're over a different calendar
        if (targetCalendarId && targetCalendarId !== event.calendarId) {
            calendarEventStore.updateField(draggedEventId, 'calendarId', targetCalendarId);
        }
    }

    handleGlobalMouseUp = (e) => {
        const { HEIGHT, HEIGHT_SCALE } = this.getHeightConfig();
        const { calendarStore, calendarEventStore } = this.props.stores;
        const {
            isDraggingEvent,
            draggedEventId,
            draggedEventOriginalStart,
            draggedEventDuration,
            draggedEventOriginalCalendarId,
            startYAbs,
        } = calendarStore;

        if (!isDraggingEvent || !draggedEventId) {
            // Clean up event listeners even if drag wasn't active
            document.removeEventListener('mousemove', this.handleGlobalMouseMove);
            document.removeEventListener('mouseup', this.handleGlobalMouseUp);
            return;
        }

        e.preventDefault();
        e.stopPropagation();

        const deltaY = e.clientY - startYAbs;
        const deltaMinutes = Math.round((deltaY / HEIGHT_SCALE) / 15) * 15;

        // Detect final target calendar and date
        const targetCalendarId = this.getCalendarIdAtPosition(e.clientX, e.clientY);
        const targetCalendarDate = this.getCalendarDateAtPosition(e.clientX, e.clientY);

        // Calculate new times based on target date (for week mode) and delta minutes
        const newStart = new Date(draggedEventOriginalStart);

        // If we have a target date (week mode), use that date but keep original time
        if (targetCalendarDate) {
            const targetDate = new Date(targetCalendarDate);
            newStart.setFullYear(targetDate.getFullYear());
            newStart.setMonth(targetDate.getMonth());
            newStart.setDate(targetDate.getDate());
        }

        // Apply time delta
        newStart.setMinutes(newStart.getMinutes() + deltaMinutes);

        const newEnd = new Date(newStart);
        newEnd.setMinutes(newEnd.getMinutes() + draggedEventDuration);

        const event = calendarEventStore.calendarEvents.find(e => e.id === draggedEventId);

        if (!event) {
            calendarStore.resetEventDrag();
            document.removeEventListener('mousemove', this.handleGlobalMouseMove);
            document.removeEventListener('mouseup', this.handleGlobalMouseUp);
            return;
        }

        // Save with potential calendar change
        const saveData = {
            start: newStart,
            end: newEnd,
        };

        // Compare against ORIGINAL calendarId, not the current one (which was updated during drag)
        if (targetCalendarId && targetCalendarId !== draggedEventOriginalCalendarId) {
            saveData.calendarId = targetCalendarId;
        }

        calendarEventStore.save(saveData, draggedEventId);

        // Reset drag state
        calendarStore.resetEventDrag();

        // Remove global event listeners
        document.removeEventListener('mousemove', this.handleGlobalMouseMove);
        document.removeEventListener('mouseup', this.handleGlobalMouseUp);
    }

    handleMouseDownPullDownHandle = e => {
        const { HEIGHT, HEIGHT_SCALE } = this.getHeightConfig();
        e.stopPropagation();
        e.preventDefault();
        const { calendarStore, calendarEventStore } = this.props.stores;

        const pullDownHandle = e.target.closest('.pull-down-handle');
        const { id } = pullDownHandle.dataset;
        const eventId = parseInt(id, 10);
        const event = calendarEventStore.calendarEvents.find(e => e.id === eventId);

        if (!event) return;

        // Don't allow resizing recurring events
        if (event.isRecurring) {
            return;
        }

        calendarStore.setDragging(true);
        calendarStore.setStartYAbs(e.clientY * HEIGHT_SCALE);
        calendarStore.setEndY(e.clientY * HEIGHT_SCALE);

        calendarStore.setEventId(eventId);
        calendarStore.setEndOfEvent(event.end);
    }

    clickSelection = e => {
        e.stopPropagation();
        e.preventDefault();
        // console.log('clickSelection');
        const { calendarStore, appState, saleStore } = this.props.stores;
        const { selectionStartDate, selectionEndDate, selectionCalendarId } = calendarStore;
        const { calendarSelectDateTime, calendarId } = this.props;
        if (calendarSelectDateTime) {
            // console.log('calendarSelectDateTime', { selectionStartDate, selectionEndDate, selectionCalendarId })
            calendarSelectDateTime({
                start: selectionStartDate,
                end: selectionEndDate,
                calendarId: selectionCalendarId,
            });
            return;
        }
        const { drawerLevel = 1, callback = () => {}, skipAnimal } = this.props;
        const { drawerHeightLarge } = appState;
        const { newSale } = saleStore;
        const { animals } = newSale;
        appState.openDrawer('newCalendarEvent', {
			height: drawerHeightLarge,
            isNew: true,
            hideAdmin: true,
            calendarType: 'booking',
            calendarId: selectionCalendarId,
            animals,
            startDate: selectionStartDate,
            endDate: selectionEndDate,
            skipAnimal,
            callback: async booking => {
                // console.log('newCalendarEvent callback', booking);
                appState.toggleDrawer(false, drawerLevel + 1);
                callback(booking);
                calendarStore.resetDragging();
            },
		}, drawerLevel + 1);
    }

    onEventMouseEnter = (domEvt, ev, currentVisitor, color, bgColor) => {
        const { userStore } = this.props.stores;
        const { customer = {} } = userStore;
        if (parseInt(customer.featureFlags?.hideCalendarPopupPreview, 10) === 1) return;
        clearTimeout(this.hoverHideTimer);
        const { left, top } = clampPos(domEvt.clientX, domEvt.clientY);
        this.hoverShowTimer = setTimeout(() => {
            this.setState({ hoverEvent: { ...ev, currentVisitor, _previewColor: color, _previewBg: bgColor }, previewPos: { left, top } });
        }, 120); // small delay prevents flicker
    };

    onEventMouseMove = (domEvt) => {
        if (!this.state.hoverEvent) return;
        if (this._rafId) return; // throttle: skip if a frame is already pending
        this._rafId = requestAnimationFrame(() => {
            this._rafId = null;
            if (!this.state.hoverEvent) return;
            const { left, top } = clampPos(domEvt.clientX, domEvt.clientY);
            this.setState({ previewPos: { left, top } });
        });
    };

    onEventMouseLeave = () => {
        clearTimeout(this.hoverShowTimer);
        this.hoverHideTimer = setTimeout(() => {
            this.setState({ hoverEvent: null });
        }, 180);
    };

    componentWillReceiveProps(nextProps) {
        if ((nextProps.calendarDate !== this.props.calendarDate) || (nextProps.calendarId !== this.props.calendarId)) {
            this.loadAll();
        }
    }

    handleKeyDown = (e) => {
        if (e.key === 'Shift' && !this.state.isShiftPressed) {
            this.setState({ isShiftPressed: true });
        }
    }

    handleKeyUp = (e) => {
        if (e.key === 'Shift') {
            this.setState({ isShiftPressed: false });
        }
    }

    componentDidMount() {
        this.loadAll();
        document.addEventListener('keydown', this.handleKeyDown);
        document.addEventListener('keyup', this.handleKeyUp);
    }

    componentWillUnmount() {
        clearTimeout(this.hoverShowTimer);
        clearTimeout(this.hoverHideTimer);
        if (this._rafId) {
            cancelAnimationFrame(this._rafId);
        }

        // Clean up global event listeners if still attached
        document.removeEventListener('mousemove', this.handleGlobalMouseMove);
        document.removeEventListener('mouseup', this.handleGlobalMouseUp);
        document.removeEventListener('keydown', this.handleKeyDown);
        document.removeEventListener('keyup', this.handleKeyUp);
    }

    getCalendarIdAtPosition = (clientX, clientY) => {
        // Use elementsFromPoint to find all elements at this position
        const elements = document.elementsFromPoint(clientX, clientY);

        // Find the first calendar-hour-grid-container in the stack
        const calendarContainer = elements.find(el =>
            el.classList && el.classList.contains('calendar-hour-grid-container')
        );

        if (calendarContainer && calendarContainer.dataset.calendarId) {
            return calendarContainer.dataset.calendarId;
        }

        return null;
    }

    getCalendarDateAtPosition = (clientX, clientY) => {
        // Use elementsFromPoint to find all elements at this position
        const elements = document.elementsFromPoint(clientX, clientY);

        // Find the first calendar-hour-grid-container in the stack
        const calendarContainer = elements.find(el =>
            el.classList && el.classList.contains('calendar-hour-grid-container')
        );

        if (calendarContainer && calendarContainer.dataset.calendarDate) {
            return calendarContainer.dataset.calendarDate;
        }

        return null;
    }

    render() {
        const { hoverEvent, previewPos, isShiftPressed } = this.state;
        const  {
            idx,
            colorIdx,
            calendarId,
            availabilityHoursStart = 0,
            availabilityMinutesStart = 0,
            availabilityHoursEnd = 0,
            availabilityMinutesEnd = 0,
            isOnlineBookingAvailability = false,
            hasOnlineBooking = false,
            onlineBookingHoursStart,
            onlineBookingMinutesStart,
            onlineBookingHoursEnd,
            onlineBookingMinutesEnd,
            useCalendarAvailability,
            showPullDownHandle,
            pulldownHandleId,
            color: colorProp,
            backgroundColor: backgroundColorProp,
            calendarDate,
            minWidth = 215,
            displayCustomerInfo,
            hideTimeColumn = false,
            showTimeBubble = true,
        } = this.props;
        // Calendar column position (0 = first column); see the hour loop below.
        const columnIdx = idx;
        const { calendarStore, calendarEventStore, userStore } = this.props.stores;
        const {
            colors,
            calendarHours,
            startY,
            startYAbs,
            endY,
            selectionStartDate,
            selectionEndDate,
            isToday,
            workhourStart,
            workhourEnd,
            isDraggingEvent,
            draggedEventId,
        } = calendarStore;

        const { user = {}, isAdmin, isTester, isVeterinary, customer = {} } = userStore;
        const darkmode= util.getNestedValue(user, 'settings.darkmode');
        const { featureFlags = {} } = customer;
        const { calendarOffDutyColor, hideCalendarPopupPreview, calendarHourHeight, eventCategories } = featureFlags;
        const HEIGHT = parseInt(calendarHourHeight, 10) || DEFAULT_HEIGHT;
        const HEIGHT_SCALE = HEIGHT / 60;
        const noAvailabilityColor = OFF_DUTY_COLORS[calendarOffDutyColor] || NO_AVAILABILITY_COLOR;

        const { dragTargetCalendarId } = calendarStore;
        const isTarget = dragTargetCalendarId === calendarId;

        const workhourStartInt = useCalendarAvailability ? parseInt(availabilityHoursStart, 10) : parseInt(availabilityHoursStart || workhourStart, 10);
        const workhourEndInt = useCalendarAvailability ? parseInt(availabilityHoursEnd || workhourEnd, 10) : parseInt(availabilityHoursEnd || workhourEnd, 10);

        // Always use normal background for work hours; online booking shown as striped overlay
        const availabilityColor = AVAILABILITY_COLOR;

        const onlineStartInt = hasOnlineBooking ? parseInt(onlineBookingHoursStart, 10) : null;
        const onlineStartMinInt = hasOnlineBooking ? parseInt(onlineBookingMinutesStart, 10) || 0 : 0;
        const onlineEndInt = hasOnlineBooking ? parseInt(onlineBookingHoursEnd, 10) : null;
        const onlineEndMinInt = hasOnlineBooking ? parseInt(onlineBookingMinutesEnd, 10) || 0 : 0;

        const today = new Date();
        const todayHour = today.getHours();
        const todayMinutes = today.getMinutes();
        const todayHourStart = new Date(today);
        todayHourStart.setHours(todayHour, 0, 0, 0);
        const isTodayLocal = calendarDate && calendarDate.toDateString() === today.toDateString();

        const selectTop = Math.min(startY, endY);
        const selectHeight = endY - startYAbs < 0 ? 0 : Math.abs(endY - startYAbs);
        // const roundedSelectHeight = Math.round(selectHeight / 15) * 15 * HEIGHT_SCALE;
        const roundedSelectHeight = Math.round((selectHeight) / 15) * 15 * HEIGHT_SCALE;

        const selectColor = colors[colors.length - 1];

        const { calendarEvents, recurringList, animals, visitors } = calendarEventStore;

        // Build lookup Maps once per render instead of .find() per event
        const animalMap = new Map();
        if (animals) {
            for (let i = 0; i < animals.length; i++) {
                animalMap.set(animals[i].id, animals[i]);
            }
        }
        const visitorMap = new Map();
        if (visitors) {
            for (let i = 0; i < visitors.length; i++) {
                visitorMap.set(visitors[i].id, visitors[i]);
            }
        }

        // Palette slot follows the calendar (colorIdx), not the column position,
        // so hiding a calendar does not shift the colours of the others.
        const paletteIdx = colorIdx >= 0 ? colorIdx : idx;
        const color = colors[paletteIdx]?.color || '#000000';
        let backgroundColor = backgroundColorProp || colors[paletteIdx]?.backgroundColor || '#ffffff';

        return (<>
            <div
                class={`no-select calendar-hour-grid-container ${isTarget ? 'calendar-drag-target' : ''}`}
                data-calendar-id={calendarId}
                data-calendar-date={util.isoDate(calendarDate, false, false, true)}
                style={isTarget ? 'box-shadow: inset 0 0 0 3px rgba(13, 110, 253, 0.5); border-radius: 4px;' : ''}
                onMouseDown={this.handleMouseDown}
                onMouseMove={this.handleMouseMove}
                onMouseUp={this.handleMouseUp}
            >

{/* startY: {startY}<br />
startYAbs: {startYAbs}<br />
endY: {endY}<br />
selectHeight: {selectHeight}<br />
roundedSelectHeight: {roundedSelectHeight}<br /> */}
{/* <xmp>{JSON.stringify({ visitors, animals }, null, 2)}</xmp> */}

                {calendarHours.map((hourDate, idx) => {
                    const hourInt = hourDate.getHours();
                    const hour = new Date(calendarDate);
                    hour.setHours(hourInt, 0, 0, 0);
                    // Conventions carry calendarId '__all__' and would repeat in
                    // every calendar column of the week/day view - show them in
                    // the first column only. NB: `idx` here is the hour index
                    // (map callback shadows the prop); the column index was
                    // captured as `columnIdx` above.
                    const events = calendarEventStore.getEvents(hour, calendarId)
                        .filter(event => !event.isConvention || !columnIdx);
                    const selectionEvent = calendarStore.getSelectionEvent(hour, calendarId);
                    const isInPast = hour < todayHourStart;
                    const isWorkHour = (hourInt >= workhourStartInt) && (hourInt < workhourEndInt);

                    return (
                        <div key={`hour-${hourInt}`} class={`d-flex flex-row`} style={`${minWidth > 0 ? `min-width: ${minWidth}px;` : ''}`}>
                            {!hideTimeColumn && <div class='d-flex flex-row justify-content-end ps-1' style='width: 38px;'>
                                <small class='text-muted font-weight-light font-style-narrow'>
                                    {util.isoTime(hour)}
                                </small>
                            </div>}
                            <div
                                class={`flex-fill ${hideTimeColumn ? '' : 'ms-1'} border-top position-relative hour-line`}
                                style={`
                                    height: ${HEIGHT}px;
                                    // opacity: ${isInPast ? 0.8 : 1};
                                    background-color: ${isWorkHour ? availabilityColor : noAvailabilityColor};
                                    border-color: ${BORDER_COLOR} !important;
                                `}
                                onClick={this.selectTime}
                                data-hour={hour.getHours()}
                                data-date={util.isoDate(calendarDate, false, false, true)}
                            >
                                {availabilityMinutesStart > 0 && hourInt === workhourStartInt && <>
                                    <div
                                        class='position-absolute'
                                        style={`
                                            top: 0px;
                                            left: 0;
                                            height: ${availabilityMinutesStart * HEIGHT_SCALE}px;
                                            min-height: ${availabilityMinutesStart * HEIGHT_SCALE}px;
                                            background-color: ${noAvailabilityColor};
                                            width: 100%;
                                            z-index: 1;
                                        `}
                                    ></div>
                                </>}
                                {availabilityMinutesEnd > 0 && hourInt === workhourEndInt && <>
                                    <div
                                        class='position-absolute'
                                        style={`
                                            top: 0;
                                            left: 0;
                                            height: ${HEIGHT - (availabilityMinutesEnd * HEIGHT_SCALE)}px;
                                            min-height: ${HEIGHT - (availabilityMinutesEnd * HEIGHT_SCALE)}px;
                                            background-color: ${availabilityColor};
                                            width: 100%;
                                            z-index: 1;
                                        `}
                                    ></div>
                                </>}
                                {hasOnlineBooking && onlineStartInt !== null && onlineEndInt !== null && (() => {
                                    const isOnlineHour = (hourInt >= onlineStartInt) && (hourInt <= onlineEndInt);
                                    if (!isOnlineHour) return null;

                                    let stripeTop = 0;
                                    let stripeHeight = HEIGHT;

                                    // Partial start hour
                                    if (hourInt === onlineStartInt && onlineStartMinInt > 0) {
                                        stripeTop = onlineStartMinInt * HEIGHT_SCALE;
                                        stripeHeight = HEIGHT - stripeTop;
                                    }
                                    // Partial end hour
                                    if (hourInt === onlineEndInt && onlineEndMinInt > 0) {
                                        stripeHeight = onlineEndMinInt * HEIGHT_SCALE - stripeTop;
                                    } else if (hourInt === onlineEndInt && onlineEndMinInt === 0) {
                                        // End hour with 0 minutes means the hour itself is not included
                                        return null;
                                    }

                                    if (stripeHeight <= 0) return null;

                                    return (
                                        <div
                                            class='position-absolute'
                                            style={`
                                                top: ${stripeTop}px;
                                                left: 0;
                                                height: ${stripeHeight}px;
                                                width: 100%;
                                                z-index: 2;
                                                pointer-events: none;
                                                background: repeating-linear-gradient(
                                                    45deg,
                                                    rgba(13, 202, 240, 0.15),
                                                    rgba(13, 202, 240, 0.15) 4px,
                                                    transparent 4px,
                                                    transparent 8px
                                                );
                                            `}
                                        />
                                    );
                                })()}
                                {isTodayLocal && hourInt === todayHour && <>
                                    {hideTimeColumn ? <>
                                        <div
                                            class='position-absolute d-flex align-items-center'
                                            style={`
                                                top: ${todayMinutes * HEIGHT_SCALE}px;
                                                left: 0;
                                                height: 0px;
                                                width: 100%;
                                                z-index: 10001;
                                                pointer-events: none;
                                            `}
                                        >
                                            {showTimeBubble && <span
                                                style={`
                                                    background-color: red;
                                                    color: white;
                                                    font-size: 10px;
                                                    font-weight: 600;
                                                    padding: 1px 4px;
                                                    border-radius: 3px;
                                                    line-height: 1.3;
                                                    white-space: nowrap;
                                                    flex-shrink: 0;
                                                `}
                                            >
                                                {`${todayHour < 10 ? '0' : ''}${todayHour}:${todayMinutes < 10 ? '0' : ''}${todayMinutes}`}
                                            </span>}
                                            <div
                                                style={`
                                                    height: 2px;
                                                    flex-grow: 1;
                                                    background-color: red;
                                                `}
                                            ></div>
                                        </div>
                                    </> : <>
                                        <div
                                            class='position-absolute d-flex align-items-center'
                                            style={`
                                                top: ${todayMinutes * HEIGHT_SCALE}px;
                                                left: -42px;
                                                height: 0px;
                                                width: calc(100% + 42px);
                                                z-index: 10001;
                                                pointer-events: none;
                                            `}
                                        >
                                            <span
                                                style={`
                                                    background-color: red;
                                                    color: white;
                                                    font-size: 10px;
                                                    font-weight: 600;
                                                    padding: 1px 4px;
                                                    border-radius: 3px;
                                                    line-height: 1.3;
                                                    white-space: nowrap;
                                                    flex-shrink: 0;
                                                `}
                                            >
                                                {`${todayHour < 10 ? '0' : ''}${todayHour}:${todayMinutes < 10 ? '0' : ''}${todayMinutes}`}
                                            </span>
                                            <div
                                                style={`
                                                    height: 2px;
                                                    flex-grow: 1;
                                                    background-color: red;
                                                `}
                                            ></div>
                                        </div>
                                    </>}
                                </>}

                                {events.map((event, eidx) => {
                                    const top = (new Date(event.start).getMinutes() / 60) * 60 * HEIGHT_SCALE;
                                    const diff = new Date(event.end) - new Date(event.start);
                                    const height = (diff / 1000 / 60 / 60) * 60 * HEIGHT_SCALE;
                                    const matchedColor = resolveEventColor(event, eventCategories);
                                    let bgColor = matchedColor || backgroundColor;

                                    let currentAnimal = {};
                                    let currentVisitor = {};
                                    if (event.animals && event.animals.length > 0) {
                                        const animalId = event.animals[0].id;
                                        const foundAnimal = animalMap.get(animalId);
                                        currentAnimal = foundAnimal ? { ...foundAnimal } : {};
                                        if (foundAnimal) {
                                            const foundVisitor = visitorMap.get(foundAnimal.owner);
                                            currentVisitor = foundVisitor ? { ...foundVisitor } : {};
                                        }
                                    }

                                    // if (event.overlapping) {
                                    //     console.log('overlapping', `eidx: ${event.overlappingIdx}: ${event.id}: ${util.isoTime(event.start)}-${util.isoTime(event.end)}: ${event.title}`, `${event.overlappingEvents.map(e => `${e.id}: ${util.isoTime(e.start)} - ${util.isoTime(e.end)}: ${e.title}`)}`)
                                    // }
                                    // console.log(event);

                                    const isBeingDragged = isDraggingEvent && draggedEventId === event.id;
                                    const isCancelled = event.offline >= 1;
                                    return (
                                        <div
                                            key={`ev-${event.id}-${eidx}`}
                                            class='position-absolute overflow-hidden ps-1 calendar-event d-flex flex-column'
                                            style={`
                                                top: ${top}px;
                                                ${event.overlapping ? ((event.overlappingIdx % 2 === 0) ? 'left: 0' : 'right: 0') : 'left: 0'};
                                                height: ${height}px;
                                                min-height: ${HEIGHT / 4}px;
                                                background-color: ${isCancelled ? 'var(--bs-secondary-bg)' : (event.hasArrived || event.arrivedTime ? localUtil.shiftColor(bgColor, 0, -70, 20) : bgColor)};
                                                color: ${isCancelled ? 'var(--bs-secondary-color)' : color};
                                                opacity: ${isBeingDragged ? 0.5 : (isCancelled ? 0.55 : 0.8)};
                                                line-height: 0.8em;
                                                width: ${event.overlapping ? '50%' : '100%'};
                                                border-left: 3px solid ${isCancelled ? 'var(--bs-danger)' : color};
                                                border-top: 1px ${isCancelled ? 'dashed' : 'solid'} ${isCancelled ? 'var(--bs-danger)' : color};
                                                text-decoration: ${isCancelled ? 'line-through' : 'none'};
                                                z-index: ${isBeingDragged ? 10001 : 10000};
                                                cursor: ${isBeingDragged ? 'grabbing' : (isCancelled ? 'pointer' : (isShiftPressed ? (event.isRecurring ? 'not-allowed' : 'grab') : 'pointer'))};
                                                box-shadow: ${isBeingDragged ? '0 4px 8px rgba(0,0,0,0.3)' : 'none'};
                                            `}
                                            data-id={event.id}
                                            data-convention-id={event.conventionId}
                                            data-start={event.start}
                                            onClick={this.viewEvent}
                                            onMouseEnter={e => this.onEventMouseEnter(e, event, currentVisitor, darkmode ? 'var(--bs-body-color)' : 'var(--bs-body-color)', darkmode ? 'var(--bs-body-bg)' : 'var(--bs-body-bg)')}
                                            onMouseMove={this.onEventMouseMove}
                                            onMouseLeave={this.onEventMouseLeave}
                                        >
                                            <small class='font-weight-normal font-style-narrow'>
                                                {isCancelled && <i class='fa-duotone fa-calendar-xmark text-danger me-1' title='Kansellert' />}
                                                {event.arrivedTime ? <>
                                                    {event.receivedTime ? <>
                                                        {event.doneTime ? <>
                                                            <i class='fa-duotone fa-check-circle text-success me-1' />
                                                        </> : <>
                                                            <i class='fa-duotone fa-user-doctor text-primary me-1' />
                                                        </>}
                                                    </> : <>
                                                        <i class='fa-solid fa-hourglass-clock fa-beat-fade text-warning me-1' />
                                                    </>}
                                                </> : <>
                                                    {event.hasArrived && <i class='fa-duotone fa-check-circle text-success me-1' />}
                                                </>}



                                                {event.skipReminders && <i class='fa-duotone fa-bell-slash text-danger me-1' />}
                                                {event.isRecurring && <i class='fa-duotone fa-repeat me-1' />}
                                                {event.isConvention && <i class='fa-duotone fa-bullseye-pointer me-1' />}
                                                <span class='font-style-narrow'>{util.isoTime(event.start)}: {event.title}</span>{event.body && <>: <span class='font-weight-light'>{event.body}</span></>}
                                                {displayCustomerInfo && <>
                                                    {currentVisitor && <>
                                                        <br />
                                                        <span class='font-weight-light'>
                                                            {currentVisitor.firstname} {currentVisitor.lastname}<br />
                                                            {currentVisitor.address}, {currentVisitor.postalcode} {currentVisitor.place}<br />
                                                            {currentVisitor.cellphone}
                                                        </span>
                                                    </>}
                                                </>}
                                                {/* {event.overlappingEvents && event.overlappingEvents.map(e => `${e.start} - ${e.end}: ${e.title}`)} */}
                                            </small>
                                            {!event.isRecurring && !isCancelled && !event.isConvention && <>
                                                <div
                                                    class='pull-down-handle position-absolute'
                                                    style={`
                                                        content: "";
                                                        display: flex;
                                                        align-items: center;
                                                        justify-content: center;
                                                        width: 50px;
                                                        height: 20px;
                                                        bottom: -2px;
                                                        left: 50%;
                                                        transform: translateX(-50%);
                                                        cursor: ns-resize;
                                                        background: transparent;
                                                        font-size: 10px;
                                                        opacity: 0;
                                                        transition: opacity 0.2s;
                                                        z-index: 10;
                                                    `}
                                                    onMouseEnter={(e) => { e.currentTarget.style.opacity = '1'; }}
                                                    onMouseLeave={(e) => { e.currentTarget.style.opacity = '0'; }}
                                                    data-id={event.id}
                                                >
                                                    <i class="fas fa-grip-lines" style="opacity: 0.7;"></i>
                                                </div>
                                            </>}
                                        </div>);
                                    lastOneIsOverlapping = event.overlapping;
                                })}
                                {hoverEvent && parseInt(hideCalendarPopupPreview, 10) !== 1 && (
                                    <EventPreview
                                        ev={hoverEvent}
                                        currentVisitor={hoverEvent.currentVisitor}
                                        pos={previewPos}
                                        color={hoverEvent._previewColor}
                                        bgColor={hoverEvent._previewBg}
                                    />
                                )}
                                {selectionEvent && <>
                                    <div
                                        class='position-absolute overflow-hidden ps-2 selection-calendar-event'
                                        data-selection={true}
                                        style={`
                                            top: ${selectTop}px;
                                            left: 0;
                                            height: ${roundedSelectHeight}px;
                                            min-height: ${HEIGHT / 4}px;
                                            background-color: ${selectColor.backgroundColor};
                                            color: ${selectColor.color};
                                            opacity: 0.5;
                                            line-height: 0.8em;
                                            width: 100%;
                                            border-left: 3px solid ${selectColor.color}};
                                            border-top: 1px solid ${selectColor.color};
                                            z-index: 10000;
                                        `}
                                        // onMouseDown={localUtil.captureEvents}
                                        // onClick={localUtil.captureEvents}
                                    >
                                        <small>
                                            {util.isoTime(selectionEvent.start)}-{util.isoTime(selectionEvent.end)}: {selectionEvent.title}
                                        </small>

                                        {/* <button type='button' class='btn btn-sm btn-success rounded-pill' onClick={this.clickSelection}>
                                            choose
                                        </button> */}

                                    </div>

                                </>}
                            </div>
                        </div>);
                })}
            </div>
        </>);
    }
}

export default CalendarHourGrid;
